import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, Lock, Mail, MessagesSquare } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SectionCard } from '../../components/design-system/admin/SectionCard';
import { useAuthContext } from '../../common/hooks/use-auth-context.hook';
import { NotificationPreferenceRow, NotificationPreferences, useNotificationsApi } from '../../common/hooks/use-notifications-api.hook';
import { ArdiChannel, useArdiApi } from '../../common/hooks/use-ardi-api.hook';
import { CHANNEL_ICONS, deliverable } from '../vendor/channels/channel-presentation';
import { CATEGORY_ICONS, CATEGORY_ORDER, categoryKey } from './notification-presentation';

/**
 * What reaches this person, and where.
 *
 * Three places, three defaults: the bell (on — it is where notifications live),
 * email (off — an inbox is only for what someone chose), and the chat apps
 * linked to Ardi (the team's, so a change there is everyone's). One row per
 * notification, grouped by what it is about, each saying when it is sent.
 */
export function NotificationSettings({ withChannels }: { withChannels: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const api = useNotificationsApi();
  const ardi = useArdiApi();

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [channels, setChannels] = useState<ArdiChannel[] | null>(withChannels ? null : []);

  useEffect(() => {
    api.getPreferences().then(setPrefs).catch(() => setPrefs({ categories: [], types: [], personal: false }));
    if (withChannels) {
      ardi
        .listChannels()
        // Only where a notification can land: a linked chat, or a webhook with an address.
        .then((r) => setChannels(r.channels.filter(deliverable)))
        .catch(() => setChannels([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withChannels, i18n.language]);

  const setPref = async (row: NotificationPreferenceRow, change: { inApp?: boolean; email?: boolean }) => {
    const before = prefs;
    setPrefs((p) => (p ? { ...p, types: p.types.map((r) => (r.type === row.type ? { ...r, ...change } : r)) } : p));
    try {
      setPrefs(await api.updatePreferences([{ type: row.type, ...change }]));
    } catch {
      setPrefs(before);
      toast.error(t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    }
  };

  const setAllEmail = async (on: boolean) => {
    if (!prefs) return;
    const changes = prefs.types.filter((r) => !r.emailedSeparately && r.email !== on).map((r) => ({ type: r.type, email: on }));
    if (!changes.length) return;
    try {
      setPrefs(await api.updatePreferences(changes));
      toast.success(t(on ? 'ARDI.NOTIFICATIONS.ALL_EMAIL_ON_DONE' : 'ARDI.NOTIFICATIONS.ALL_EMAIL_OFF_DONE'));
    } catch {
      toast.error(t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    }
  };

  const setChannelType = async (channel: ArdiChannel, type: string, on: boolean) => {
    const notifyTypes = on ? [...new Set([...channel.notifyTypes, type])] : channel.notifyTypes.filter((x) => x !== type);
    setChannels((list) => list?.map((c) => (c.uuid === channel.uuid ? { ...c, notifyTypes } : c)) ?? list);
    try {
      const saved = await ardi.updateChannel(channel.uuid, { notifyTypes });
      setChannels((list) => list?.map((c) => (c.uuid === channel.uuid ? saved : c)) ?? list);
    } catch {
      setChannels((list) => list?.map((c) => (c.uuid === channel.uuid ? channel : c)) ?? list);
      toast.error(t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    }
  };

  const grouped = useMemo(() => {
    if (!prefs) return [];
    return CATEGORY_ORDER.filter((c) => prefs.categories.includes(c)).map((category) => ({
      category,
      rows: prefs.types.filter((r) => r.category === category),
    }));
  }, [prefs]);

  if (!prefs || !channels) {
    return (
      <div className="flex flex-col gap-4">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
      </div>
    );
  }

  const emailsOn = prefs.types.filter((r) => r.email).length;
  const columns = `minmax(0,1fr) 5.5rem 5.5rem${channels.map(() => ' 5.5rem').join('')}`;

  return (
    <div className="flex flex-col gap-6" data-testid="notification-settings">
      {/* Where notifications can go, before the list of what. */}
      <div className="grid gap-3 md:grid-cols-3">
        <PlaceCard icon={Bell} title={t('ARDI.NOTIFICATIONS.PLACE.BELL')} body={t('ARDI.NOTIFICATIONS.PLACE.BELL_BODY')} />
        <PlaceCard
          icon={Mail}
          title={t('ARDI.NOTIFICATIONS.PLACE.EMAIL')}
          body={t('ARDI.NOTIFICATIONS.PLACE.EMAIL_BODY', { email: (user as any)?.email ?? '' })}
          footer={
            <Button variant="outline" size="sm" onClick={() => setAllEmail(emailsOn === 0)}>
              {t(emailsOn ? 'ARDI.NOTIFICATIONS.ALL_EMAIL_OFF' : 'ARDI.NOTIFICATIONS.ALL_EMAIL_ON')}
            </Button>
          }
        />
        {withChannels && (
          <PlaceCard
            icon={MessagesSquare}
            title={t('ARDI.NOTIFICATIONS.PLACE.CHANNELS')}
            body={channels.length ? t('ARDI.NOTIFICATIONS.PLACE.CHANNELS_BODY', { count: channels.length }) : t('ARDI.NOTIFICATIONS.PLACE.CHANNELS_NONE')}
            footer={
              <Button variant="outline" size="sm" onClick={() => navigate('/home/channels')}>
                {t(channels.length ? 'ARDI.NOTIFICATIONS.MANAGE_CHANNELS' : 'ARDI.NOTIFICATIONS.CONNECT_CHANNEL')}
              </Button>
            }
          />
        )}
      </div>

      {grouped.map(({ category, rows }) => {
        const Icon = CATEGORY_ICONS[category];
        return (
          <SectionCard
            key={category}
            flush
            title={
              <span className="flex items-center gap-2">
                <Icon className="size-4 text-muted-foreground" aria-hidden />
                {t(categoryKey(category))}
              </span>
            }
            // Staff read support from the other side: vendors writing in, not replies.
            description={t(`ARDI.NOTIFICATIONS.CATEGORY_DESC.${category.toUpperCase()}${!withChannels && category === 'support' ? '_STAFF' : ''}`)}
            testId={`prefs-${category}`}
          >
            <div className="overflow-x-auto border-t">
              <div className="min-w-fit">
                <div className="grid items-center gap-x-2 border-b bg-muted/40 px-6 py-2 text-xs text-muted-foreground" style={{ gridTemplateColumns: columns }}>
                  <span>{t('ARDI.NOTIFICATIONS.COLUMN.NOTIFICATION')}</span>
                  <span className="text-center">{t('ARDI.NOTIFICATIONS.COLUMN.BELL')}</span>
                  <span className="text-center">{t('ARDI.NOTIFICATIONS.COLUMN.EMAIL')}</span>
                  {channels.map((c) => {
                    const ChannelIcon = CHANNEL_ICONS[c.kind];
                    return (
                      <span key={c.uuid} className="flex items-center justify-center gap-1 truncate" title={c.name}>
                        <ChannelIcon className="size-3.5 shrink-0" aria-hidden />
                        <span className="truncate">{c.name}</span>
                      </span>
                    );
                  })}
                </div>
                <ul className="divide-y">
                  {rows.map((row) => (
                    <li key={row.type} className="grid items-center gap-x-2 px-6 py-3" style={{ gridTemplateColumns: columns }} data-testid={`pref-${row.type}`}>
                      <div className="min-w-0 pe-4">
                        <p className="text-sm">{row.label}</p>
                        <p className="text-xs leading-relaxed text-muted-foreground">{row.about}</p>
                      </div>
                      <div className="flex justify-center">
                        {row.lockedInApp ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 text-xs text-muted-foreground" tabIndex={0}>
                                <Lock className="size-3.5" aria-hidden />
                                <span className="sr-only">{t('ARDI.NOTIFICATIONS.LOCKED')}</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{t('ARDI.NOTIFICATIONS.LOCKED')}</TooltipContent>
                          </Tooltip>
                        ) : (
                          <Switch
                            checked={row.inApp}
                            onCheckedChange={(v) => setPref(row, { inApp: v })}
                            aria-label={t('ARDI.NOTIFICATIONS.TOGGLE_BELL', { name: row.label }) as string}
                          />
                        )}
                      </div>
                      <div className="flex justify-center">
                        {row.emailedSeparately ? (
                          <span className="text-center text-[11px] leading-tight text-muted-foreground">{t('ARDI.NOTIFICATIONS.EMAILED_SEPARATELY')}</span>
                        ) : (
                          <Switch
                            checked={row.email}
                            disabled={!prefs.personal}
                            onCheckedChange={(v) => setPref(row, { email: v })}
                            aria-label={t('ARDI.NOTIFICATIONS.TOGGLE_EMAIL', { name: row.label }) as string}
                          />
                        )}
                      </div>
                      {channels.map((c) => (
                        <div key={c.uuid} className={cn('flex justify-center')}>
                          <Switch
                            checked={c.notifyTypes.includes(row.type)}
                            onCheckedChange={(v) => setChannelType(c, row.type, v)}
                            aria-label={t('ARDI.NOTIFICATIONS.TOGGLE_CHANNEL', { name: row.label, channel: c.name }) as string}
                          />
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </SectionCard>
        );
      })}
      {withChannels && channels.length > 0 && (
        <p className="px-1 text-xs text-muted-foreground">{t('ARDI.NOTIFICATIONS.CHANNELS_SHARED')}</p>
      )}
    </div>
  );
}

const PlaceCard: React.FC<{ icon: React.ComponentType<{ className?: string }>; title: string; body: string; footer?: React.ReactNode }> = ({ icon: Icon, title, body, footer }) => (
  <Card className="gap-0 py-0">
    <CardContent className="flex h-full flex-col gap-2 p-4">
      <span className="flex size-8 items-center justify-center rounded-lg border bg-background text-muted-foreground">
        <Icon className="size-4" />
      </span>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">{body}</p>
      {footer && <div className="mt-auto pt-1">{footer}</div>}
    </CardContent>
  </Card>
);
